import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useLanguage } from '../../hooks/useLanguage';
import { User } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';

interface UserCardProps {
  user: User;
  onBan: (userId: string, userName: string, isBanned: boolean) => void;
  onRoleChange: (userId: string, userName: string, isAdmin: boolean) => void;
  onDelete: (userId: string, userName: string) => void;
  isTogglingBan?: boolean;
  isUpdatingRole?: boolean;
  isDeleting?: boolean;
}

export function UserCard({
  user,
  onBan,
  onRoleChange,
  onDelete,
  isTogglingBan = false,
  isUpdatingRole = false,
  isDeleting = false,
}: UserCardProps) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const userName = `${user.firstName} ${user.lastName || ''}`;

  return (
    <div className="card-glass rounded-lg p-4 hover:border-brand-accent/50 transition-all duration-300">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h3 className="text-lg font-heading font-semibold text-neutral-light mb-1">
              {userName}
            </h3>
            {user.username && (
              <p className="text-sm text-neutral-medium font-body">
                @{user.username}
              </p>
            )}
          </div>
        </div>

        {/* Contact Info */}
        {(user.email || user.telegramId) && (
          <div className="space-y-1">
            {user.email && (
              <div>
                <span className="text-xs text-neutral-medium font-body block mb-1">
                  {t('admin.users.table.contact')}
                </span>
                <p className="text-sm text-neutral-light font-body break-all">
                  {user.email}
                </p>
              </div>
            )}
            {user.telegramId && (
              <p className="text-xs text-neutral-medium font-body">
                Telegram: {user.telegramId}
              </p>
            )}
            {!user.telegramId && user.legacyTelegramId && (
              <p className="text-xs text-neutral-medium font-body flex items-center gap-1">
                Telegram: {user.legacyTelegramId}
                <span className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-body bg-brand-accent/20 text-brand-accent">
                  {t('admin.users.details.info.migrated')}
                </span>
              </p>
            )}
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.users.table.balance')}
            </span>
            <span className="text-sm font-heading font-semibold text-brand-accent">
              {parseFloat(user.balance.toString()).toFixed(2)} USD
            </span>
            {user.referralBalance > 0 && (
              <p className="text-xs text-neutral-medium font-body mt-1">
                Ref: {parseFloat(user.referralBalance.toString()).toFixed(2)}
              </p>
            )}
          </div>
          <div>
            <span className="text-xs text-neutral-medium font-body block mb-1">
              {t('admin.users.table.status')}
            </span>
            <div className="flex flex-col gap-1">
              <span
                className={`inline-block px-2 py-1 rounded-full text-xs font-body w-fit ${
                  user.isBanned
                    ? 'bg-danger/20 text-danger'
                    : 'bg-green-500/20 text-green-500'
                }`}
              >
                {user.isBanned
                  ? t('admin.users.status.banned')
                  : t('admin.users.status.active')}
              </span>
              {user.isAdmin && (
                <span className="inline-block px-2 py-1 rounded-full text-xs font-body w-fit bg-brand-accent/20 text-brand-accent">
                  {t('admin.users.status.admin')}
                </span>
              )}
              {user.role === 'admin_support' && (
                <span className="inline-block px-2 py-1 rounded-full text-xs font-body w-fit bg-blue-500/20 text-blue-500">
                  {t('admin.users.status.support')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Date */}
        <div>
          <span className="text-xs text-neutral-medium font-body">
            {t('admin.users.table.created')}: {formatDate(user.originalJoinedAt || user.createdAt, 'PP h:mm a', currentLanguage)}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-neutral-dark">
          <Link
            to={`/admin/users/${user._id}`}
            className="flex-1 px-3 py-2 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body text-center"
            title={t('admin.users.actions.view')}
          >
            {t('admin.users.actions.view')}
          </Link>
          <button
            onClick={() => onBan(user._id, userName, user.isBanned)}
            disabled={isTogglingBan}
            className={`flex-1 px-3 py-2 rounded-lg transition-colors text-sm font-body ${
              user.isBanned
                ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                : 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
            }`}
            title={
              user.isBanned
                ? t('admin.users.actions.unban')
                : t('admin.users.actions.ban')
            }
          >
            {user.isBanned
              ? t('admin.users.actions.unban')
              : t('admin.users.actions.ban')}
          </button>
          {!user.isAdmin && (
            <button
              onClick={() => onRoleChange(user._id, userName, user.isAdmin)}
              disabled={isUpdatingRole}
              className="flex-1 px-3 py-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30 transition-colors text-sm font-body"
              title={t('admin.users.actions.makeAdmin')}
            >
              {t('admin.users.actions.makeAdmin')}
            </button>
          )}
          {user.isAdmin && (
            <button
              onClick={() => onRoleChange(user._id, userName, user.isAdmin)}
              disabled={isUpdatingRole}
              className="flex-1 px-3 py-2 bg-orange-500/20 text-orange-500 rounded-lg hover:bg-orange-500/30 transition-colors text-sm font-body"
              title={t('admin.users.actions.removeAdmin')}
            >
              {t('admin.users.actions.removeAdmin')}
            </button>
          )}
          {!user.isAdmin && (
            <button
              onClick={() => onDelete(user._id, userName)}
              disabled={isDeleting}
              className="flex-1 px-3 py-2 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-body disabled:opacity-50"
              title={t('admin.users.actions.delete')}
            >
              {t('admin.users.actions.delete')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
